"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useAppContext } from "@/context/AppContext";
import { Eye, EyeOff } from "lucide-react";

interface LoginFormProps {
  onSwitch?: () => void;
}

export default function LoginForm({ onSwitch }: LoginFormProps) {
  const router = useRouter();
  const { toast } = useToast();
  const { login } = useAppContext();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please fill in both fields.");
      return;
    }

    setIsLoading(true);

    try {
      const user = await login(email, password);

      if (user) {
        toast({
          title: "Login successful",
          description: `Welcome back, ${user.name || "User"}!`,
          variant: "success",
        });

        // Close login modal automatically
        if (typeof window !== "undefined") {
          const evt = new CustomEvent("closeAuthModal");
          window.dispatchEvent(evt);
        }

        // Navigate to the correct dashboard
        router.push(
          user.role === "admin"
            ? "/master-admin"
            : user.role === "company"
              ? "/company-admin"
              : "/worker-portal",
        );
      } else {
        setError("Invalid email or password.");
      }
    } catch (err: any) {
      console.error("Login error:", err);

      // Interpret backend messages clearly
      let message = "Something went wrong. Please try again.";

      if (err.message?.toLowerCase().includes("deactivated")) {
        message =
          "Your company account has been deactivated. Please contact support.";
      } else if (err.message?.toLowerCase().includes("expired")) {
        message =
          "Your company subscription has expired. Please renew your plan.";
      } else if (err.message?.toLowerCase().includes("invalid")) {
        message = "Invalid email or password.";
      } else if (err.message) {
        message = err.message;
      }

      toast({
        title: "Login failed",
        description: message,
        variant: "destructive",
      });

      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative mx-auto w-full max-w-md overflow-hidden rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/40 dark:backdrop-blur-2xl shadow-lg transition-all duration-300">
      {/* Header */}
      <div className="text-center border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 dark:backdrop-blur-md py-5">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Welcome Back
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          Log in to your account
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-6 space-y-5">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white border border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-emerald-500"
            required
          />
        </div>

        <div>
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-white border border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-emerald-500"
              required
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-emerald-600"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={() => setRememberMe(!rememberMe)}
              className="mr-2 accent-emerald-600"
            />
            Remember me
          </label>
          <button
            type="button"
            onClick={() => router.push("/forgot-password")}
            className="text-sm text-emerald-600 hover:underline"
          >
            Forgot password?
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300 p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/30 active:scale-95 transition-all"
          disabled={isLoading}
        >
          {isLoading ? "Logging in..." : "Login"}
        </Button>

        <p className="text-sm text-center text-gray-700 dark:text-gray-400">
          Don’t have an account?{" "}
          <button
            type="button"
            onClick={onSwitch}
            className="text-emerald-600 hover:underline font-medium"
          >
            Sign up
          </button>
        </p>
      </form>
    </div>
  );
}
